import { Component, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, computed, signal, input, effect, NgZone, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TimeEntry, TimeSlot } from '../../../../shared/models/activity.model';
import { MiniModalDetailTimeEntryComponent } from './mini-modal-detail-entry/mini-modal-detail-entry';
import { GoogleCalendarEvent } from '../../../../shared/services/google-calendar.service';


// GRID TO ENTER AND SHOW TIME ENTRIES
@Component({
  selector: 'app-time-grid',
  imports: [CommonModule, FormsModule, MiniModalDetailTimeEntryComponent],
  templateUrl: './time-grid.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class TimeGridComponent implements AfterViewInit, OnDestroy {
  // Signal inputs (remplace @Input)
  zoom = input(15);
  slots = input<TimeSlot[]>([]);
  timeEntries = input<TimeEntry[]>([]);
  calendarEvents = input<GoogleCalendarEvent[]>([]);
  rowHeight = input(32);
  currentLineTop = input(-1);
  hoveredSlotIndex = input<number | null>(null);


  @Output() selectionMade = new EventEmitter<{ startTime: Date; endTime: Date }>();
  @Output() hoverSlot = new EventEmitter<number | null>();

  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('hourColumn') hourColumn!: ElementRef<HTMLDivElement>;

  // Internal signals (remplace les propriétés mutables)
  isSelecting = signal(false);
  selectionStartIndex = signal<number | null>(null);
  selectionEndIndex = signal<number | null>(null);
  selectedEntry = signal<TimeEntry | null>(null);
  modalPosition = signal({ top: 0, left: 0 });
  hourColumnWidth = signal(0);

  // Throttle pour mousemove
  private lastMouseMoveTime = 0;
  private readonly THROTTLE_MS = 16; // ~60fps
  private mouseMoveListener: ((e: MouseEvent) => void) | null = null;
  private mouseUpListener: ((e: MouseEvent) => void) | null = null;

  // Computed: slot map pour lookup O(1)
  private slotMap = computed(() => {
    const map = new Map<number, TimeSlot>();
    for (const s of this.slots()) map.set(s.index, s);
    return map;
  });

  // Computed: entries avec lanes, couleurs et durations pré-calculées
  entriesWithLanes = computed(() => {
    const entries = [...this.timeEntries()];
    const sorted = entries.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    const lanesEnd: number[] = [];

    for (const entry of sorted) {
      let assigned = false;
      for (let lane = 0; lane < lanesEnd.length; lane++) {
        if (entry.startTime.getTime() >= lanesEnd[lane]) {
          entry.lane = lane;
          lanesEnd[lane] = entry.endTime.getTime();
          assigned = true;
          break;
        }
      }
      if (!assigned) {
        entry.lane = lanesEnd.length;
        lanesEnd.push(entry.endTime.getTime());
      }
      entry.color = this.getProjectColor(entry.projectName);
    }
    return { entries: sorted, maxLanes: lanesEnd.length || 1 };
  });

  // Computed: placements map avec duration pré-calculée
  placements = computed(() => {
    const map = new Map<string, { top: number; height: number; left: string; width: string; duration: string }>();
    const { entries, maxLanes } = this.entriesWithLanes();
    const slotsArr = this.slots();
    const rh = this.rowHeight();
    const z = this.zoom();

    if (!slotsArr.length) return map;

    for (const entry of entries) {
      if (!entry.id) continue;
      const diffMinutes = (entry.startTime.getTime() - slotsArr[0].time.getTime()) / 60000;
      const durationMinutes = (entry.endTime.getTime() - entry.startTime.getTime()) / 60000;

      // Pré-calculer la duration string
      const diff = entry.endTime.getTime() - entry.startTime.getTime();
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const durationStr = hours < 1 ? `${minutes}m` : `${hours}h ${minutes}m`;

      map.set(entry.id, {
        top: diffMinutes * (rh / z),
        height: durationMinutes * (rh / z),
        left: `calc(60px + (${entry.lane} * ((100% - 60px) / ${maxLanes})) + 2px)`,
        width: `calc((100% - 60px) / ${maxLanes})`,
        duration: durationStr
      });
    }
    return map;
  });

  // Computed: calendar event placements (right-aligned column)
  calendarPlacements = computed(() => {
    const events = this.calendarEvents();
    const slotsArr = this.slots();
    const rh = this.rowHeight();
    const z = this.zoom();
    if (!slotsArr.length || !events.length) return [];

    const gridStart = slotsArr[0].time.getTime();

    return events.map(ev => {
      const startMs = ev.start.dateTime
        ? new Date(ev.start.dateTime).getTime()
        : new Date(ev.start.date!).getTime();
      const endMs = ev.end.dateTime
        ? new Date(ev.end.dateTime).getTime()
        : new Date(ev.end.date!).getTime() + 86400000;

      const diffMinutes = (startMs - gridStart) / 60000;
      const durationMinutes = (endMs - startMs) / 60000;
      const totalMinutes = (endMs - startMs);
      const hours = Math.floor(totalMinutes / 3600000);
      const minutes = Math.floor((totalMinutes % 3600000) / 60000);
      const durationStr = hours < 1 ? `${minutes}m` : `${hours}h ${minutes}m`;

      return {
        id: ev.id,
        summary: ev.summary,
        description: ev.description,
        durationStr,
        top: Math.max(0, diffMinutes * (rh / z)),
        height: Math.max(rh, durationMinutes * (rh / z)),
        startTime: new Date(startMs),
        endTime: new Date(endMs),
      };
    });
  });


  // Computed: set des slots de sélection
  selectionSlots = computed(() => {
    const start = this.selectionStartIndex();
    const end = this.selectionEndIndex();
    if (start === null || end === null) return new Set<number>();

    const min = Math.min(start, end);
    const max = Math.max(start, end);
    const set = new Set<number>();
    for (let i = min; i <= max; i++) set.add(i);
    return set;
  });

  // Computed: entries par slot (pour vérifier si un slot a des entries)
  entriesPerSlot = computed(() => {
    const map = new Map<number, TimeEntry[]>();
    const slotMapVal = this.slotMap();
    const { entries } = this.entriesWithLanes();

    for (const [index, slot] of slotMapVal) {
      const t = slot.time.getTime();
      map.set(index, entries.filter(e => t >= e.startTime.getTime() && t < e.endTime.getTime()));
    }
    return map;
  });

  constructor(private ngZone: NgZone, private cdr: ChangeDetectorRef) {
    // Reset selection quand zoom change
    effect(() => {
      this.zoom(); // track dependency
      this.selectionStartIndex.set(null);
      this.selectionEndIndex.set(null);
    });
  }

  ngAfterViewInit() {
    this.hourColumnWidth.set(this.hourColumn?.nativeElement.offsetWidth ?? 0);
    this.setupMouseListeners();
  }

  ngOnDestroy() {
    this.removeMouseListeners();
  }

  private setupMouseListeners() {
    // Exécuter en dehors de Angular zone pour éviter change detection excessive
    this.ngZone.runOutsideAngular(() => {
      this.mouseMoveListener = (event: MouseEvent) => {
        const now = performance.now();
        if (now - this.lastMouseMoveTime < this.THROTTLE_MS) return;
        this.lastMouseMoveTime = now;
        this.handleMouseMove(event);
      };

      this.mouseUpListener = (event: MouseEvent) => {
        this.ngZone.run(() => this.onMouseUp());
      };

      document.addEventListener('mouseup', this.mouseUpListener);
    });
  }

  private removeMouseListeners() {
    if (this.mouseUpListener) {
      document.removeEventListener('mouseup', this.mouseUpListener);
    }
  }

  private handleMouseMove(event: MouseEvent) {
    if (!this.scrollContainer || !this.isSelecting()) return;
    const container = this.scrollContainer.nativeElement;
    const rect = container.getBoundingClientRect();
    const relativeY = event.clientY - rect.top + container.scrollTop;
    const index = Math.floor(relativeY / this.rowHeight());

    if (index >= 0 && index < this.slots().length) {
      const currentEnd = this.selectionEndIndex();
      if (currentEnd !== index) {
        this.ngZone.run(() => {
          this.selectionEndIndex.set(index);
        });
      }
    }
  }

  onContainerMouseMove(event: MouseEvent) {
    // Throttle
    const now = performance.now();
    if (now - this.lastMouseMoveTime < this.THROTTLE_MS) return;
    this.lastMouseMoveTime = now;

    if (!this.scrollContainer) return;
    const container = this.scrollContainer.nativeElement;
    const rect = container.getBoundingClientRect();
    const relativeY = event.clientY - rect.top + container.scrollTop;
    const index = Math.floor(relativeY / this.rowHeight());
    if (index >= 0 && index < this.slots().length) this.onMouseEnter(index);
  }

  onMouseDown(index: number) {
    if ((this.entriesPerSlot().get(index)?.length ?? 0) > 0) return;
    this.isSelecting.set(true);
    this.selectionStartIndex.set(index);
    this.selectionEndIndex.set(index);
  }

  onMouseEnter(index: number) {
    if (this.isSelecting() && this.selectionStartIndex() !== null) {
      const currentEnd = this.selectionEndIndex();
      if (currentEnd !== index) {
        this.selectionEndIndex.set(index);
      }
    }
    this.hoverSlot.emit(index);
  }

  onMouseLeaveGrid() {
    this.hoverSlot.emit(null);
  }

  onMouseUp() {
    if (!this.isSelecting()) return;
    if (this.selectionStartIndex() !== null && this.selectionEndIndex() !== null) {
      this.emitSelection();
    }
    this.isSelecting.set(false);
  }

  private emitSelection() {
    const start = Math.min(this.selectionStartIndex()!, this.selectionEndIndex()!);
    const end = Math.max(this.selectionStartIndex()!, this.selectionEndIndex()!);
    const slotMapVal = this.slotMap();
    const startSlot = slotMapVal.get(start);
    const endSlot = slotMapVal.get(end);
    if (!startSlot || !endSlot) return;

    const startTime = startSlot.time;
    const endTime = new Date(endSlot.time);
    endTime.setMinutes(endTime.getMinutes() + this.zoom());
    this.selectionMade.emit({ startTime, endTime });
    this.selectionStartIndex.set(null);
    this.selectionEndIndex.set(null);
  }

  getProjectColor(projectName: string): string {
    let hash = 0;
    for (let i = 0; i < (projectName ?? '').length; i++) hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 75%, 85%)`;
  }

  openDetail(entry: TimeEntry, event: MouseEvent) {
    event.stopPropagation();
    this.selectedEntry.set(entry);
    this.modalPosition.set({ top: event.clientY - 10, left: event.clientX - 300 });
  }

  closeDetail() {
    this.selectedEntry.set(null);
  }
}
